import type { McpPolicyRawToolset } from './policyTypes';

export const MCP_DEFAULT_SCOPES = [
  'read:me',
  'read:account',
  'offline_access',
] as const;

/**
 * OIDC scopes that aren't declared by any toolset. They are passed through to
 * the resume form only when they were present in the original authorization
 * request (the context JWT); otherwise they'd always be filtered out during
 * consent because no toolset selection ever includes them.
 */
export const MCP_PASSTHROUGH_OIDC_SCOPES = [
  'openid',
  'email',
  'profile',
] as const;

/** Union of scopes for the given toolset names from a list of raw toolsets; each scope appears once. */
export function unionScopesForToolsetNames(
  toolsetNames: Iterable<string>,
  toolsets: McpPolicyRawToolset[],
): string[] {
  const byName = new Map(toolsets.map((t) => [t.name, t]));
  const out = new Set<string>();
  for (const name of toolsetNames) {
    const def = byName.get(name);
    if (!def) continue;
    for (const s of def.scopes) {
      out.add(s);
    }
  }
  return [...out].sort();
}

/**
 * Scopes for the resume form: union of scopes across the selected toolsets.
 * No JWT intersection — the API already ensures toolsets are within the authorized scope set.
 * Always includes the MCP_DEFAULT_SCOPES regardless of toolset selection.
 * Additionally passes through any MCP_PASSTHROUGH_OIDC_SCOPES (openid/email/profile)
 * that were in the original request (`requestedScopes`), since no toolset declares them.
 */
export function buildMcpSelectedScopesForPost(
  selectedNames: Iterable<string>,
  toolsets: McpPolicyRawToolset[],
  requestedScopes: Iterable<string> = [],
): string[] {
  const requested = new Set(requestedScopes);
  return [
    ...new Set([
      ...MCP_DEFAULT_SCOPES,
      ...unionScopesForToolsetNames(selectedNames, toolsets),
      ...MCP_PASSTHROUGH_OIDC_SCOPES.filter((s) => requested.has(s)),
    ]),
  ];
}
